import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Divider, Form, Input, Popconfirm, Select, Spin, Tag, message as antMessage } from "antd";
import { IdcardOutlined } from "@ant-design/icons";
import { SelectClient } from "@controls";
import Client from "../clients/client";
import { serverFetch } from "@server";
import { fmtDate } from "@extensions/utils";
import {
  TAG_OBJECT_VID_CLIENTS,
  TAG_OBJECT_VID_DIALOGS,
} from "../settings/tags/tags-const";

/** HEX для отображения (#…), как в настройках тегов */
const dialogTagHexColor = (raw) => {
  if (raw == null || raw === "") return null;
  const s = String(raw).trim();
  return s.startsWith("#") ? s : `#${s}`;
};

/** Подпись в SelectClient как в записи: «Имя Телефон» */
const formatClientSelectLabel = (name, phone) =>
  [name, phone]
    .map((x) => (x == null ? "" : String(x).trim()))
    .filter(Boolean)
    .join(" ");

const getDialogTags = (dialog, fallbackTags) =>
  Array.isArray(dialog?.tags) ? dialog.tags : Array.isArray(fallbackTags) ? fallbackTags : [];

const DialogMain = ({ dialog: dialogProp, tags = [], onReload, onClose }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [usersList, setUsersList] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [agentsList, setAgentsList] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [tagCatalog, setTagCatalog] = useState([]);
  const [loadingTags, setLoadingTags] = useState(true);
  const [clientId, setClientId] = useState(null);
  const [clientOptionLabel, setClientOptionLabel] = useState("");
  const [clientCardOpen, setClientCardOpen] = useState(false);
  const [clientDetail, setClientDetail] = useState(null);
  const [loadingClientDetail, setLoadingClientDetail] = useState(false);

  const refetchClientDetail = useCallback(() => {
    if (clientId == null) return;
    setLoadingClientDetail(true);
    serverFetch(`client/${clientId}`)
      .then((data) => {
        setClientDetail(data);
        form.setFieldsValue({
          enteredName: data.name ?? "",
          enteredPhone: data.phone ?? "",
          enteredEmail: data.email ?? "",
        });
        setClientOptionLabel(formatClientSelectLabel(data.name, data.phone) || `#${clientId}`);
      })
      .catch((e) => {
        antMessage.error(e.userMessage);
        setClientDetail(null);
      })
      .finally(() => setLoadingClientDetail(false));
  }, [clientId, form]);

  useEffect(() => {
    setLoadingUsers(true);
    serverFetch("user")
      .then((list) => setUsersList(Array.isArray(list) ? list : []))
      .catch(() => setUsersList([]))
      .finally(() => setLoadingUsers(false));
  }, []);

  useEffect(() => {
    setLoadingAgents(true);
    serverFetch("Agent")
      .then((list) => setAgentsList(Array.isArray(list) ? list : []))
      .catch(() => setAgentsList([]))
      .finally(() => setLoadingAgents(false));
  }, []);

  useEffect(() => {
    setLoadingTags(true);
    serverFetch("Tag", {
      queryParams: { objectVid: [TAG_OBJECT_VID_DIALOGS] },
    })
      .then((list) => setTagCatalog(Array.isArray(list) ? list : []))
      .catch(() => setTagCatalog([]))
      .finally(() => setLoadingTags(false));
  }, []);

  useEffect(() => {
    if (!dialogProp) return;
    const dialogTags = getDialogTags(dialogProp, tags);
    setClientCardOpen(false);
    const cid = dialogProp.clientId ?? null;
    setClientId(cid);
    setClientOptionLabel(
      cid != null ? formatClientSelectLabel(dialogProp.clientName, dialogProp.clientPhone) || `#${cid}` : ""
    );
    form.setFieldsValue({
      name: dialogProp.name ?? "",
      userId: dialogProp.userId ?? undefined,
      agentId: dialogProp.agentId ?? undefined,
      tagIds: dialogTags.filter((t) => Number(t.objectVid) === TAG_OBJECT_VID_DIALOGS).map((t) => t.id),
      note: dialogProp.note ?? "",
    });
    if (cid == null) {
      setClientDetail(null);
      form.setFieldsValue({
        enteredName: dialogProp.enteredName ?? "",
        enteredPhone: dialogProp.enteredPhone ?? "",
        enteredEmail: dialogProp.enteredEmail ?? "",
      });
    } else {
      form.setFieldsValue({
        enteredName: "",
        enteredPhone: "",
        enteredEmail: "",
      });
    }
  }, [dialogProp, tags, form]);

  useEffect(() => {
    if (clientId == null) {
      setClientDetail(null);
      return;
    }
    let cancelled = false;
    setLoadingClientDetail(true);
    serverFetch(`client/${clientId}`)
      .then((data) => {
        if (cancelled) return;
        setClientDetail(data);
        form.setFieldsValue({
          enteredName: data.name ?? "",
          enteredPhone: data.phone ?? "",
          enteredEmail: data.email ?? "",
        });
      })
      .catch((e) => {
        if (!cancelled) {
          antMessage.error(e.userMessage);
          setClientDetail(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingClientDetail(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, dialogProp?.id, form]);

  useEffect(() => {
    if (clientId == null) setClientCardOpen(false);
  }, [clientId]);

  const { tagSelectOptions, tagById } = useMemo(() => {
    const opts = tagCatalog.map((t) => ({
      value: t.id,
      label: t.value ?? "",
      tagColor: t.color,
    }));
    const map = new Map(tagCatalog.map((t) => [t.id, t]));
    return { tagSelectOptions: opts, tagById: map };
  }, [tagCatalog]);

  const editBlocked = Boolean(dialogProp?.deleted);

  const onFinish = (values) => {
    if (!dialogProp || editBlocked) return;
    setSaving(true);
    const currentTags = getDialogTags(dialogProp, tags);
    const dialogTagIds = values.tagIds ?? [];
    const clientUnchanged =
      clientId != null &&
      dialogProp.clientId != null &&
      clientId === dialogProp.clientId;
    const clientTagIds = clientUnchanged
      ? currentTags.filter((t) => Number(t.objectVid) === TAG_OBJECT_VID_CLIENTS).map((t) => t.id)
      : [];
    const nextTags = [...dialogTagIds, ...clientTagIds].map((id) => ({ id }));

    /** Имя/телефон/email с клиента только для отображения; в БД не трогаем entered* при привязанном клиенте */
    const persistEnteredInDb = clientId == null;
    const nameVal = persistEnteredInDb ? values.enteredName ?? "" : dialogProp.enteredName ?? "";
    const phoneVal = persistEnteredInDb ? values.enteredPhone ?? "" : dialogProp.enteredPhone ?? "";
    const emailVal = persistEnteredInDb ? values.enteredEmail ?? "" : dialogProp.enteredEmail ?? "";

    serverFetch("Message/Dialog", {
      method: "PUT",
      bodyData: {
        ...dialogProp,
        name: values.name ?? "",
        enteredName: nameVal,
        enteredPhone: phoneVal,
        enteredEmail: emailVal,
        userId: values.userId ?? null,
        agentId: values.agentId ?? null,
        clientId: clientId ?? null,
        note: values.note ?? "",
        tags: nextTags,
      },
    })
      .then(() => {
        antMessage.success("Сохранено");
        onReload?.();
      })
      .catch((e) => antMessage.error(e.userMessage))
      .finally(() => setSaving(false));
  };

  const onDeleteDialog = () => {
    if (!dialogProp?.id) return;
    setActionLoading("delete");
    serverFetch(`Message/DialogDelete/${dialogProp.id}`, { method: "DELETE" })
      .then(() => {
        antMessage.success("Диалог удалён");
      })
      .catch((e) => antMessage.error(e.userMessage))
      .finally(() => setActionLoading(null));
  };

  const onUndeleteDialog = () => {
    if (!dialogProp?.id) return;
    setActionLoading("undelete");
    serverFetch(`Message/DialogUndelete/${dialogProp.id}`, { method: "DELETE" })
      .then(() => {
        antMessage.success("Диалог восстановлен");
        onReload?.();
      })
      .catch((e) => antMessage.error(e.userMessage))
      .finally(() => setActionLoading(null));
  };

  if (!dialogProp) return null;

  const userOptions = usersList.map((u) => ({
    value: u.id,
    label: u.name ?? u.shortName ?? `#${u.id}`,
  }));

  const agentOptions = agentsList
    .filter((a) => a.active || a.id === dialogProp.agentId)
    .map((a) => ({
      value: a.id,
      label: a.active ? (a.name || `#${a.id}`) : `${a.name || `#${a.id}`} (неактивен)`,
    }));

  return (
    <div className="dialog-detail">
      <div className="dialog-detail-contact">
        <Spin spinning={loadingUsers || loadingAgents || loadingTags || loadingClientDetail}>
          <Form
            form={form}
            layout="horizontal"
            labelCol={{ flex: "130px" }}
            wrapperCol={{ flex: "1" }}
            colon={false}
            onFinish={onFinish}
            requiredMark={false}
            className="dialog-detail-contact-form"
          >
            <Form.Item name="name" label="Наименование">
              <Input
                placeholder="Краткое название обращения"
                style={{ width: "100%" }}
                disabled={editBlocked}
              />
            </Form.Item>
            <Form.Item label="Клиент">
              <div className="dialog-client-field-row">
                <SelectClient
                  placeholder="Выберите клиента"
                  style={{ flex: 1, minWidth: 0 }}
                  disabled={editBlocked}
                  value={clientId ?? undefined}
                  defOptions={
                    clientId != null ? [{ value: clientId, label: clientOptionLabel || `#${clientId}` }] : []
                  }
                  getInitialNewClientFields={() => {
                    const v = form.getFieldsValue(["enteredName", "enteredPhone", "enteredEmail"]);
                    const patch = {};
                    const name = (v.enteredName ?? "").toString().trim();
                    const phone = (v.enteredPhone ?? "").toString().trim();
                    const email = (v.enteredEmail ?? "").toString().trim();
                    if (name) patch.name = name;
                    if (phone) patch.phone = phone;
                    if (email) patch.email = email;
                    return Object.keys(patch).length ? patch : null;
                  }}
                  onChange={(e) => {
                    if (!e) return;
                    setClientId(e.value);
                    setClientOptionLabel(e.label ?? `#${e.value}`);
                  }}
                  onClear={() => {
                    setClientId(null);
                    setClientOptionLabel("");
                    setClientDetail(null);
                    form.setFieldsValue({
                      enteredName: dialogProp.enteredName ?? "",
                      enteredPhone: dialogProp.enteredPhone ?? "",
                      enteredEmail: dialogProp.enteredEmail ?? "",
                    });
                  }}
                />
                <Button
                  type="default"
                  className="dialog-client-card-btn"
                  icon={<IdcardOutlined />}
                  disabled={clientId == null || editBlocked}
                  title="Карточка клиента"
                  aria-label="Открыть карточку клиента"
                  onClick={() => setClientCardOpen(true)}
                />
              </div>
            </Form.Item>
            {clientCardOpen && clientId != null ? (
              <Client
                id={clientId}
                onSaved={refetchClientDetail}
                onClose={() => setClientCardOpen(false)}
              />
            ) : null}
            <Form.Item name="enteredName" label="Имя">
              <Input
                placeholder="ФИО"
                style={{ width: "100%" }}
                readOnly={clientId != null || editBlocked}
                disabled={editBlocked}
                className={clientId != null || editBlocked ? "dialog-detail-readonly-input" : undefined}
              />
            </Form.Item>
            <Form.Item name="enteredPhone" label="Телефон">
              <Input
                placeholder="+7 …"
                style={{ width: "100%" }}
                readOnly={clientId != null || editBlocked}
                disabled={editBlocked}
                className={clientId != null || editBlocked ? "dialog-detail-readonly-input" : undefined}
              />
            </Form.Item>
            <Form.Item name="enteredEmail" label="Email">
              <Input
                placeholder="email"
                style={{ width: "100%" }}
                readOnly={clientId != null || editBlocked}
                disabled={editBlocked}
                className={clientId != null || editBlocked ? "dialog-detail-readonly-input" : undefined}
              />
            </Form.Item>
            <Form.Item name="userId" label="Менеджер">
              <Select
                allowClear
                placeholder="Не назначен"
                options={userOptions}
                optionFilterProp="label"
                showSearch
                style={{ width: "100%" }}
                disabled={editBlocked}
              />
            </Form.Item>
            <Form.Item name="agentId" label="Агент">
              <Select
                allowClear
                placeholder="Не указан"
                options={agentOptions}
                optionFilterProp="label"
                showSearch
                style={{ width: "100%" }}
                disabled={editBlocked}
              />
            </Form.Item>
            <Form.Item name="tagIds" label="Теги">
              <Select
                mode="multiple"
                allowClear
                placeholder="Выберите теги"
                options={tagSelectOptions}
                optionFilterProp="label"
                showSearch
                style={{ width: "100%" }}
                disabled={editBlocked}
                optionRender={(oriOption) => {
                  const d = oriOption.data;
                  const hex = dialogTagHexColor(d?.tagColor);
                  return (
                    <span className="dialog-tag-option-row">
                      <span
                        className="dialog-tag-swatch"
                        aria-hidden
                        style={{ background: hex ?? "#d9d9d9" }}
                      />
                      <span>{d?.label ?? ""}</span>
                    </span>
                  );
                }}
                tagRender={(props) => {
                  const { label, value, closable, onClose } = props;
                  const meta = tagById.get(value);
                  const hex = dialogTagHexColor(meta?.color);
                  return (
                    <Tag
                      closable={closable}
                      onClose={onClose}
                      style={{
                        marginInlineEnd: 4,
                        ...(hex
                          ? {
                              backgroundColor: hex,
                              borderColor: hex,
                              color: "#fff",
                            }
                          : {}),
                      }}
                    >
                      {meta?.value ?? label}
                    </Tag>
                  );
                }}
              />
            </Form.Item>
            <Form.Item name="note" label="Примечание">
              <Input.TextArea
                placeholder="Многострочное примечание"
                autoSize={{ minRows: 3, maxRows: 8 }}
                style={{ width: "100%" }}
                disabled={editBlocked}
              />
            </Form.Item>
            <Divider className="dialog-detail-divider-compact" />
            <Form.Item label="#" className="dialog-detail-readonly-row">
              <Input
                readOnly
                className="dialog-detail-readonly-input"
                value={String(dialogProp.id ?? "")}
                style={{ width: "100%" }}
              />
            </Form.Item>
            <Form.Item label="Компания">
              <Input
                readOnly
                className="dialog-detail-readonly-input"
                value={dialogProp.companyName ?? ""}
                style={{ width: "100%" }}
              />
            </Form.Item>
            <Form.Item label="Активность">
              <Input
                readOnly
                className="dialog-detail-readonly-input"
                value={`${fmtDate(dialogProp.createdOn) ?? "—"} - ${fmtDate(dialogProp.lastUpdated) ?? "—"}`}
                style={{ width: "100%" }}
              />
            </Form.Item>
            <Form.Item label="Канал">
              <Input
                readOnly
                className="dialog-detail-readonly-input"
                value={
                  [dialogProp.channelName, dialogProp.externalId]
                    .map((x) => (x == null ? "" : String(x).trim()))
                    .filter(Boolean)
                    .join(" ") || "—"
                }
                style={{ width: "100%" }}
              />
            </Form.Item>
            <div className="dialog-detail-submit-wrap">
              <Button className="dialog-client-card-bottom-btn" disabled={clientId == null || editBlocked} onClick={() => setClientCardOpen(true)}>
                В карточку
              </Button>
              {dialogProp.deleted ? (
                <Popconfirm title="Подтверждение" description="Восстановить диалог?" onConfirm={onUndeleteDialog}>
                  <Button loading={actionLoading === "undelete"}>Восстановить</Button>
                </Popconfirm>
              ) : (
                <Popconfirm title="Подтверждение" description="Удалить диалог?" onConfirm={onDeleteDialog}>
                  <Button danger loading={actionLoading === "delete"}> Удалить </Button>
                </Popconfirm>
              )}
              <Button type="primary" htmlType="submit" loading={saving} disabled={editBlocked}> Сохранить </Button>
            </div>
          </Form>
        </Spin>
      </div>
    </div>
  );
};

export default DialogMain;
